import React, { useEffect } from "react";
import { Card, CardContent, Typography, Button, Box } from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import { PaymentType, VirtualCardResponse } from "../../types/paymentTypes";
import { CardData } from "../../types/common";
import { useDispatch } from "react-redux";
import { setCardData } from "../../redux/slices/vccSlice";
import { useErrorBoundary } from "react-error-boundary";
import { formatCurrency, getMethodProviderName } from "../../common/helpers";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface ManageCardProps {
  data: VirtualCardResponse;
  showButton?: boolean;
  buttonLabel?: string;
  buttonHandler?: (balance: number) => void;
}

const ManageCard: React.FC<ManageCardProps> = ({
  data,
  showButton = true,
  buttonHandler,
  buttonLabel,
}) => {
  const dispatch = useDispatch();
  const { showBoundary } = useErrorBoundary();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (data) {
      const transformed: CardData = {
        target_account_id: data.id,
        card_number: data.card_number,
        balance: data.balance,
        type: PaymentType.VirtualCard,
        method_provider: getMethodProviderName(data.provider),
      };
      dispatch(setCardData(transformed));
    }
  }, [data, dispatch]);

  return (
    <Card
      variant="outlined"
      sx={{ border: "none", backgroundColor: "#123B42" }}
    >
      <CardContent
        sx={{
          position: "relative",
          backgroundImage: "url(/card/frog.svg)",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right top",
          backgroundSize: "auto 80%",
          color: "white",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">
            {formatCurrency(data.balance, false)}
          </Typography>
        </Box>

        <Typography variant="body2">{t("vcc_availableBalance")}</Typography>

        {showButton && (
          <Button
            startIcon={<PaymentIcon />}
            variant="outlined"
            sx={{
              marginTop: 2,
              color: "white",
              borderColor: "white",
              ":hover": { borderColor: "white" },
            }}
            onClick={() => {
              if (buttonHandler) buttonHandler(data.balance ?? 0);
              else navigate(`/card/details/${data.provider.toLowerCase()}`);
            }}
          >
            {buttonLabel ?? t("card_manage")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ManageCard;
